import Transition from "@shared/core/Transition";
import theme from "@shared/utils/themeColors";
import React, { useEffect, useRef, useState } from "react";

export interface DropdownItem {
  id: string;
  label: string;
  icon?: React.ReactNode;
  className?: string;
  onClick?: () => void;
  href?: string;
}

interface DropdownProps {
  horizontalAlign?: "left" | "right";
  verticalAlign?: "top" | "bottom";
  trigger: React.ReactNode;
  items: DropdownItem[];
  title?: string;
  className?: string;
  triggerClassName?: string;
  itemClassName?: string;
  titleClassName?: string;
}

const Dropdown: React.FC<DropdownProps> = ({
  horizontalAlign = "left",
  verticalAlign = "bottom",
  trigger,
  items,
  title,
  className = "",
  triggerClassName = "",
  itemClassName = "",
  titleClassName = "",
}) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close on click outside
  useEffect(() => {
    const clickHandler = ({ target }: MouseEvent) => {
      if (!dropdownRef.current) return;
      if (
        !dropdownOpen ||
        dropdownRef.current.contains(target as Node) ||
        triggerRef.current?.contains(target as Node)
      )
        return;
      setDropdownOpen(false);
    };

    document.addEventListener("click", clickHandler);
    return () => document.removeEventListener("click", clickHandler);
  });

  // Close if the esc key is pressed
  useEffect(() => {
    const keyHandler = ({ keyCode }: KeyboardEvent) => {
      if (!dropdownOpen || keyCode !== 27) return;
      setDropdownOpen(false);
    };

    document.addEventListener("keydown", keyHandler);
    return () => document.removeEventListener("keydown", keyHandler);
  });

  return (
    <div className={`relative inline-flex ${className}`}>
      <button
        ref={triggerRef}
        className={triggerClassName}
        aria-haspopup="true"
        onClick={() => setDropdownOpen(!dropdownOpen)}
        aria-expanded={dropdownOpen}
      >
        {trigger}
      </button>

      <Transition
        className={`origin-top-right z-10 absolute min-w-44 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700/60 py-1.5 rounded-lg shadow-lg overflow-hidden mt-1 ${
          horizontalAlign === "right" ? "right-0" : "left-0"
        } ${verticalAlign === "top" ? "bottom-full" : "top-full"}`}
        show={dropdownOpen}
        enter="transition ease-out duration-200 transform"
        enterStart="opacity-0 -translate-y-2"
        enterEnd="opacity-100 translate-y-0"
        leave="transition ease-out duration-200"
        leaveStart="opacity-100"
        leaveEnd="opacity-0"
      >
        <div
          ref={dropdownRef}
          onFocus={() => setDropdownOpen(true)}
          onBlur={() => setDropdownOpen(false)}
        >
          {title && (
            <div
              className={`text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase pt-1.5 pb-2 px-3 ${titleClassName}`}
            >
              {title}
            </div>
          )}
          <ul>
            {items.map((item) => (
              <li key={item.id}>
                {item.href ? (
                  <a
                    href={item.href}
                    className={`font-medium text-sm ${theme.text.brand} ${theme.text.brandHover} flex items-center py-1 px-3 ${itemClassName} ${item.className}`}
                    onClick={() => {
                      setDropdownOpen(false);
                      item.onClick?.();
                    }}
                  >
                    {item.icon && <span className="mr-2">{item.icon}</span>}
                    <span>{item.label}</span>
                  </a>
                ) : (
                  <button
                    className={`w-full text-left font-medium text-sm ${theme.text.brand} ${theme.text.brandHover} flex items-center py-1 px-3 ${itemClassName} ${item.className}`}
                    onClick={() => {
                      setDropdownOpen(false);
                      item.onClick?.();
                    }}
                  >
                    {item.icon && <span className="mr-2">{item.icon}</span>}
                    <span>{item.label}</span>
                  </button>
                )}
              </li>
            ))}
          </ul>
        </div>
      </Transition>
    </div>
  );
};

export default Dropdown;
